<template>
  <CodSheet v-model="isOpen" width="480px">
    <div class="p-5">
      <UAlert color="error" variant="solid" :title="t('subscription.heading')">
        <template #description>
          <div v-if="shouldShowDebtAmount()">
            <span>
              <strong>{{ t('subscription.debt_amount', { value: debt.toFormat(), software: 'Konty' }) }}</strong>
              <br>
            </span>
            <span>{{ t('subscription.debt_settle') }} </span>
            <span>{{ t('subscription.debt_invoices') }}</span>
          </div>
          <div v-else>
            <span>{{ t('subscription.debt_settle_reseller', { software: 'Konty' }) }} </span>
          </div>
        </template>
      </UAlert>
    </div>

    <template #footer>
      <UButton size="lg"
        color="neutral"
        variant="outline"
        :label="t('general.close')"
        @click="hide"
      />
    </template>
  </CodSheet>
</template>

<script setup lang="ts">
// The daily debt warning of a contract-billed tenant. An inactive tenant gets the account lock
// (AccountInactiveLock.vue) instead.
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n';
import { useAppStore } from '@/stores/useAppStore'

import { AppStorageKey, IAppStorage, TenantService, diContainer } from 'app-core';
import { Money } from 'app-core';
import { CodSheet } from 'app-components';
import moment from 'moment-timezone'

const { t } = useI18n()
const store = useAppStore()

const appStorage = diContainer.resolve<IAppStorage>('appStorage')

const isOpen = ref(false)
const debt = ref(Money.parse(0))

if(import.meta.env.VITE_ENABLE_BILLING === 'true') {
  debt.value = store?.tenant?.company
    ? Money.parse(store.tenant.company.amount_due)
    : Money.parse(0)

  showDebtWarning()
}

watch(() => store.tenant, async (val, oldVal) => {
  if(import.meta.env.VITE_ENABLE_BILLING !== 'true') return

  debt.value = store?.tenant?.company
    ? Money.parse(store.tenant.company.amount_due)
    : Money.parse(0)

  showDebtWarning()
})

function hide() {
  isOpen.value = false
}

async function showDebtWarning() {
  if(TenantService.isInactive(store.tenant)) return

  if(debt.value.toUnit() < 2) return

  if(moment().hour() <= 5) return

  const today = moment().format('YYYY-MM-DD')
  const debtWarningSeenDateAt = await appStorage.local().getItem<string>(AppStorageKey.debtWarningSeenDate);

  if(!debtWarningSeenDateAt || today !== debtWarningSeenDateAt) {
    appStorage.local().setItem(AppStorageKey.debtWarningSeenDate, today);
    isOpen.value = true
  }
}

function shouldShowDebtAmount() {
  return store.tenant?.company?.latest_invoice?.path
}
</script>
